import type { CommitmentLine, DateRange, LocalDate, ModuleId, PlannedLine } from '@tahi/core';
import type { LocalDb } from './localDb';

/**
 * A source of payments due for Budget's cash flow (spec: Fixed bills, Loans, Card installments,
 * Utilities). Payments registers one in v0.5. `tables` are the device tables `load` reads, so
 * Budget screens re-read when they change. Budget never reads another module's tables itself.
 * `today` is Budget's today (the user's time zone, like commands), e.g. for overdue dues.
 */
export interface CommitmentsProvider {
  id: string;
  moduleId: ModuleId;
  tables: readonly string[];
  load(db: LocalDb, userId: string, range: DateRange, today: LocalDate): Promise<CommitmentLine[]>;
}

/**
 * A source of planned spending shown beside "What's left" and never subtracted from it (spec §6,
 * Shopping list: "₱3,200 left · ₱850 on your shopping list"). The Shopping list module registers one
 * (key 'shopping') from its register.tsx; v0.2 Budget alone registers none.
 */
export interface PlannedSpendingProvider {
  id: string;
  moduleId: ModuleId;
  tables: readonly string[];
  /** Open items right now (planned spending has no date of its own). */
  load(db: LocalDb, userId: string): Promise<PlannedLine[]>;
}

const providers = new Map<string, CommitmentsProvider>();
const plannedProviders = new Map<string, PlannedSpendingProvider>();

export function registerCommitmentsProvider(p: CommitmentsProvider): void {
  providers.set(p.id, p);
}

export function registerPlannedSpendingProvider(p: PlannedSpendingProvider): void {
  plannedProviders.set(p.id, p);
}

export function listCommitmentsProviders(): CommitmentsProvider[] {
  return [...providers.values()];
}

/** Every device table a commitments or planned-spending provider reads. */
export function commitmentTables(): string[] {
  return [
    ...new Set([...providers.values(), ...plannedProviders.values()].flatMap((p) => [...p.tables])),
  ];
}

/** Commitment lines for the range from every provider whose module is usable. */
export async function loadCommitments(
  db: LocalDb,
  userId: string,
  range: DateRange,
  usable: (id: ModuleId) => boolean,
  today: LocalDate,
): Promise<CommitmentLine[]> {
  const out: CommitmentLine[] = [];
  for (const p of providers.values()) {
    if (!usable(p.moduleId)) continue;
    out.push(...(await p.load(db, userId, range, today)));
  }
  return out;
}

/** Planned-spending lines from every provider whose module is usable. */
export async function loadPlanned(
  db: LocalDb,
  userId: string,
  usable: (id: ModuleId) => boolean,
): Promise<PlannedLine[]> {
  const out: PlannedLine[] = [];
  for (const p of plannedProviders.values()) {
    if (!usable(p.moduleId)) continue;
    out.push(...(await p.load(db, userId)));
  }
  return out;
}

/** Tests only. */
export function resetCommitmentsProviders(): void {
  providers.clear();
  plannedProviders.clear();
}
